import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { MapPinIcon, ArrowLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useState, useMemo } from 'react';
import { api } from '../lib/api';
import SEO from '../components/SEO';

interface CityInfo {
  city: string;
  state: string;
  count: number;
  slug: string;
}

export default function StatePage() {
  const { stateSlug } = useParams<{ stateSlug: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Get state name from slug (we'll need to reverse the slugification)
  const getStateName = (slug: string) => {
    // This is a simple approach - in production you might want a more robust mapping
    return slug.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const stateName = stateSlug ? getStateName(stateSlug) : '';

  const { data: cities = [], isLoading, error } = useQuery({
    queryKey: ['cities-by-state', stateSlug],
    queryFn: () => stateSlug ? api.locations.getCitiesByState(stateName) : Promise.resolve([]),
    enabled: !!stateSlug,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });

  // Filter cities based on search query
  const filteredCities = useMemo(() => {
    if (!searchQuery.trim()) return cities;
    return cities.filter(city => 
      city.city.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [cities, searchQuery]);

  if (isLoading) {
    return (
      <div className="page-container">
        <SEO
          title={`Summer Camps in ${stateName} - CampWhiz.com`}
          description={`Find summer camps in ${stateName} by city. Browse local camps organized by location.`}
          keywords={`summer camps ${stateName}, camps by city ${stateName}, local camps ${stateName}`}
          url={`/camps-by-city/${stateSlug}`}
        />
        <div className="page-content">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading cities in {stateName}...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <SEO
          title={`Summer Camps in ${stateName} - CampWhiz.com`}
          description={`Find summer camps in ${stateName} by city. Browse local camps organized by location.`}
          keywords={`summer camps ${stateName}, camps by city ${stateName}, local camps ${stateName}`}
          url={`/camps-by-city/${stateSlug}`}
        />
        <div className="page-content">
          <div className="text-center">
            <h1 className="text-3xl font-semibold text-gray-900 mb-4">Error Loading Cities</h1>
            <p className="text-gray-600 mb-6">There was an error loading the city information for {stateName}. Please try again later.</p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <SEO
        title={`Summer Camps in ${stateName} - CampWhiz.com`}
        description={`Find summer camps in ${stateName} by city. Browse local camps organized by location.`}
        keywords={`summer camps ${stateName}, camps by city ${stateName}, local camps ${stateName}`}
        url={`/camps-by-city/${stateSlug}`}
      />
      
      <div className="page-content">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
            Summer Camps in {stateName}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find camps organized by city in {stateName}. Click on a city to see all available camps.
          </p>
        </div>

        {/* Back Button */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/camps-by-city')}
            className="inline-flex items-center text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to All States
          </button>
        </div>

        {/* City Search */}
        <div className="mb-8">
          <div className="max-w-md mx-auto">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Find your city..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            {searchQuery && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Showing {filteredCities.length} of {cities.length} cities
              </p>
            )}
          </div>
        </div>

        {/* Cities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredCities.map((cityInfo: CityInfo) => (
            <Link
              key={cityInfo.city}
              to={`/camps-by-city/${stateSlug}/${cityInfo.slug}`}
              className="group bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 p-6 border border-gray-200 hover:border-primary"
            >
              <div className="flex items-center justify-between mb-4">
                <MapPinIcon className="h-8 w-8 text-primary group-hover:text-primary/80 transition-colors" />
                <span className="text-sm font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                  {cityInfo.count} camp{cityInfo.count !== 1 ? 's' : ''}
                </span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 group-hover:text-primary transition-colors">
                {cityInfo.city}
              </h2>
            </Link>
          ))}
        </div>

        {/* No results message */}
        {searchQuery && filteredCities.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No cities found matching "{searchQuery}"</p>
            <button
              onClick={() => setSearchQuery('')}
              className="text-primary hover:text-primary/80 mt-2"
            >
              Clear search
            </button>
          </div>
        )}


      </div>
    </div>
  );
}
